import React, { useContext } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';

import ApiContext from 'api/ApiContext';
import { Linklist } from '@ktag/styleguide/dist/react';
import { Issue } from 'api/api.types';
import AdditionalResourcesBox from './AdditionalResourcesBox';
import useTransformDownloads from './useTransformDownloads';

interface Props {
  id: string;
  issues?: Issue[];
  headingLevel?: 2 | 3;
}

const Downloads = ({ id, issues, headingLevel }: Props) => {
  const { t } = useTranslation('wap');

  const { getDownloads } = useContext(ApiContext);

  // type is polling_days when called from PollPage.tsx and issues, when called from Poll.tsx
  const type = issues ? 'polling_days' : 'issues';
  const query = useQuery(['downloads', id], () => getDownloads(id, type));
  const { formattedIssueDownloads, downloadsGroups } = useTransformDownloads(
    type,
    query,
    issues,
    id,
  );

  downloadsGroups.sort((a, b) => a.sortKey - b.sortKey);

  // Prop headingLevel references the headingLevel of AdditionalResourcesBox.
  // We need to increase it by 1 to get the correct heading level for the sub title.
  const HeadingTag = `h${headingLevel + 1}` as 'h2' | 'h3';

  return (
    <AdditionalResourcesBox title={t('downloads')} headingLevel={headingLevel}>
      {type === 'issues' && <Linklist links={formattedIssueDownloads} />}
      {type === 'polling_days' &&
        downloadsGroups.map((group) => (
          <React.Fragment key={group.label}>
            <HeadingTag className="wap__additional-resources-box-sub-title h4">
              {group.label}
            </HeadingTag>
            <Linklist links={group.downloads} />
          </React.Fragment>
        ))}
    </AdditionalResourcesBox>
  );
};

Downloads.defaultProps = {
  headingLevel: 2,
};

export default Downloads;
